import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'antd';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import pluralize from 'pluralize';
import cx from 'classnames';
import queryString from 'query-string';
import { useDidUpdate } from 'rooks';

import {
  getAgingSummaryReport,
  getAgingSummaryReportExcel,
} from 'redux/reducers/homeDashboardSlice';
import { SortingDirectionEnum } from 'enums';
import SortButton from 'components/wrappers/BaseSortButton';
import { ISortParams } from 'hooks/useSortColumns';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import { pageDefaultSizeTable } from 'constants/index';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import LastSyncDateSection from 'components/LastSyncDateSection';
import { isShowFooterTableOnLastPage } from 'utils/tableSettings';
import {
  AgingDetailDateRangeTypeEnum,
  AgingSummaryReport,
  AgingSummaryReportPagedResponse,
  AgingSummarySortFieldEnum,
} from 'api/backofficeApiClient';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './AgingReportSummaryDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

const defaultSortParams = {
  sortOrder: SortingDirectionEnum.Ascend,
  sortBy: AgingSummarySortFieldEnum.Customer,
};

export default function AgingReportSummaryDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sortOrder, sortBy } = queryString.parse(location.search);
  const initialSortParams =
    sortBy && sortOrder
      ? { sortBy: parseInt(sortBy), sortOrder }
      : defaultSortParams;
  const [sortParam, setSortParam] =
    useState<ISortParams<AgingSummarySortFieldEnum>>(initialSortParams);
  const [agingReportData, setAgingReportData] =
    useState<AgingSummaryReportPagedResponse>();
  const [isAgingReportDataLoading, setIsAgingReportDataLoading] =
    useState(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const dispatch = useAppDispatch();

  const agingReportCountMessage = pluralize(
    'record',
    agingReportData?.totalCount,
    true,
  );

  const columns = useMemo(
    () => [
      {
        title: () => {
          return (
            <SortButton
              isActive={true}
              sortType={sortParam.sortOrder}
              label="Customer Name"
            />
          );
        },
        key: AgingSummarySortFieldEnum.Customer,
        render: (record: AgingSummaryReport) => (
          <Link className="wrapped-text" to={`/customer/${record.customerKey}`}>
            {record.customerCompanyName}
          </Link>
        ),
        sorter: true,
        sortDirections: [
          SortingDirectionEnum.Ascend,
          SortingDirectionEnum.Descend,
        ],
        sortOrder: sortParam.sortOrder,
      },
      {
        title: 'Customer ID',
        key: 'customerId',
        render: (record: AgingSummaryReport) => record.customerKey,
      },
      {
        title: 'Terms',
        key: 'terms',
        render: (record: AgingSummaryReport) => record.terms,
      },
      {
        title: '≤ 30 Days',
        key: 'lessThan30Balance',
        render: (record: AgingSummaryReport) => (
          <Link
            to={`/aging-report-details/${record.customerKey}/${AgingDetailDateRangeTypeEnum.LessThan30}`}
          >
            {record.lessThan30Balance}
          </Link>
        ),
      },
      {
        title: '31 - 60 Days',
        key: 'moreThan30Balance',
        render: (record: AgingSummaryReport) => (
          <Link
            to={`/aging-report-details/${record.customerKey}/${AgingDetailDateRangeTypeEnum.MoreThan30}`}
          >
            {record.moreThan30Balance}
          </Link>
        ),
      },
      {
        title: '61 - 90 Days',
        key: 'moreThan60Balance',
        render: (record: AgingSummaryReport) => (
          <Link
            to={`/aging-report-details/${record.customerKey}/${AgingDetailDateRangeTypeEnum.MoreThan60}`}
          >
            {record.moreThan60Balance}
          </Link>
        ),
      },
      {
        title: '> 90 Days',
        key: 'moreThan90Balance',
        render: (record: AgingSummaryReport) => (
          <Link
            to={`/aging-report-details/${record.customerKey}/${AgingDetailDateRangeTypeEnum.MoreThan90}`}
          >
            {record.moreThan90Balance}
          </Link>
        ),
      },
      {
        title: 'Total Balance',
        key: 'totalBalance',
        render: (record: AgingSummaryReport) => (
          <Link to={`/aging-report-total-balance/${record.customerKey}`}>
            {record.totalBalance}
          </Link>
        ),
      },
    ],
    [sortParam],
  );

  useDidUpdate(() => {
    const queryData = {
      sortOrder: sortParam.sortOrder,
      sortBy: sortParam.sortBy,
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [sortParam]);

  const loadAgingReportData = useCallback(async () => {
    setIsAgingReportDataLoading(true);

    const result = await dispatch(
      getAgingSummaryReport({
        pageNumber: currentTablePage - 1,
        sortField: sortParam.sortBy,
        sortType: getSortOrderRequestValue(sortParam.sortOrder),
        pageSize: pageDefaultSizeTable,
      }),
    );

    if (getAgingSummaryReport.fulfilled.match(result)) {
      setAgingReportData(result.payload);
    } else {
      setAgingReportData({
        totalCount: 0,
        details: [],
      });
    }

    setIsAgingReportDataLoading(false);
  }, [dispatch, currentTablePage, sortParam]);

  const loadInitialData = useCallback(async () => {
    await loadAgingReportData();

    setIsInitialDataLoaded(true);
  }, [loadAgingReportData]);

  const handleDownloadReport = async () => {
    const result = await dispatch(
      getAgingSummaryReportExcel({
        pageNumber: currentTablePage - 1,
        sortField: sortParam.sortBy,
        sortType: getSortOrderRequestValue(sortParam.sortOrder),
        pageSize: pageDefaultSizeTable,
      }),
    );

    if (getAgingSummaryReportExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort: SorterResult<AgingSummaryReport> | SorterResult<AgingSummaryReport>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<AgingSummaryReport>).order ||
      SortingDirectionEnum.Ascend;
    const sortBy = (sort as SorterResult<AgingSummaryReport>)
      .columnKey as AgingSummarySortFieldEnum;

    setSortParam(({ sortBy: prevSortBy, sortOrder: prevSortOrder }) => {
      if (prevSortBy !== sortBy || prevSortOrder !== sortOrder) {
        setCurrentTablePage(1);
      } else {
        setCurrentTablePage(pagination.current || 1);
      }

      return { sortOrder, sortBy };
    });
  };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {agingReportData?.lastSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={agingReportData.lastSyncDate}
                title="Aging report is automatically refreshed every 10 minutes."
              />
            </div>
          )}
        </BasePageLabel>

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              title="AR Aging - Summary"
              enableSubTitle={isInitialDataLoaded}
              subTitle={agingReportCountMessage}
            >
              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>

            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: agingReportData?.totalCount,
                pageSize: pageDefaultSizeTable,
                current: currentTablePage,
                onChange: setCurrentTablePage,
                showSizeChanger: false,
              }}
              showSorterTooltip={false}
              summary={() => {
                if (
                  !isInitialDataLoaded ||
                  !isShowFooterTableOnLastPage(
                    agingReportData?.totalCount,
                    pageDefaultSizeTable,
                    agingReportData?.pageNumber,
                  )
                )
                  return;

                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={3} index={1}>
                      Total
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <Link
                        to={`/aging-report-total-details/${AgingDetailDateRangeTypeEnum.LessThan30}`}
                      >
                        {agingReportData?.totalLessThan30Balance}
                      </Link>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <Link
                        to={`/aging-report-total-details/${AgingDetailDateRangeTypeEnum.MoreThan30}`}
                      >
                        {agingReportData?.totalMoreThan30Balance}
                      </Link>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      <Link
                        to={`/aging-report-total-details/${AgingDetailDateRangeTypeEnum.MoreThan60}`}
                      >
                        {agingReportData?.totalMoreThan60Balance}
                      </Link>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      <Link
                        to={`/aging-report-total-details/${AgingDetailDateRangeTypeEnum.MoreThan90}`}
                      >
                        {agingReportData?.totalMoreThan90Balance}
                      </Link>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <Link
                        to={`/aging-report-total-details/${AgingDetailDateRangeTypeEnum.MoreThen0}`}
                      >
                        {agingReportData?.totalBalance}
                      </Link>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
              onChange={handleTableChange}
              columns={columns}
              dataSource={agingReportData?.details}
              rowKey={(record) => record.customerCompanyName as string}
              loading={isAgingReportDataLoading}
              scroll={{ x: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
